<template>
  <div class="video-creation-page">
    <!-- 页面标题 -->
    <div class="page-header">
      <h1>
        <el-icon><Film /></el-icon>
        视频创作
      </h1>
      <p>输入视频脚本，AI自动生成分镜图和提示词</p>
    </div>

    <div class="main-content">
      <!-- 左侧：聊天区域 -->
      <div class="chat-panel">
        <div class="chat-messages" ref="messagesContainer">
          <!-- 欢迎消息 -->
          <div class="message assistant" v-if="messages.length === 0">
            <div class="message-avatar">
              <el-icon><Film /></el-icon>
            </div>
            <div class="message-content">
              <p>👋 你好！我是AI视频创作助手。</p>
              <p>请输入你的视频脚本内容，我会帮你：</p>
              <ul>
                <li>将脚本拆分为分镜场景</li>
                <li>生成分镜图提示词</li>
                <li>生成图生视频提示词</li>
                <li>使用AI绘制分镜图</li>
              </ul>
              <p>你也可以选择下方的示例脚本快速体验~</p>
            </div>
          </div>

          <!-- 消息列表 -->
          <div 
            v-for="(msg, idx) in messages" 
            :key="idx" 
            :class="['message', msg.role]"
          >
            <div class="message-avatar">
              <el-icon v-if="msg.role === 'assistant'"><Film /></el-icon>
              <el-icon v-else><User /></el-icon>
            </div>
            <div class="message-content">
              <div v-html="formatMessage(msg.content)"></div>
            </div>
          </div>

          <!-- 加载中 -->
          <div class="message assistant" v-if="loading">
            <div class="message-avatar">
              <el-icon><Film /></el-icon>
            </div>
            <div class="message-content">
              <el-icon class="is-loading"><Loading /></el-icon>
              正在分析脚本...
            </div>
          </div>
        </div>

        <!-- 示例脚本 -->
        <div class="example-scripts" v-if="messages.length === 0">
          <span class="label">示例脚本：</span>
          <el-tag 
            v-for="example in exampleScripts" 
            :key="example.title"
            class="example-tag"
            @click="useExample(example)"
            effect="plain"
          >
            {{ example.title }}
          </el-tag>
        </div>

        <!-- 输入区域 -->
        <div class="chat-input">
          <el-input
            v-model="inputText"
            type="textarea"
            :rows="3"
            placeholder="输入视频脚本内容，例如：一个年轻女孩走在秋天的街道上..."
            @keydown.enter.ctrl="sendMessage"
          />
          <div class="input-actions">
            <el-button type="primary" :loading="loading" @click="sendMessage">
              <el-icon><Position /></el-icon>
              分析脚本
            </el-button>
            <el-button @click="clearChat" :disabled="loading">
              <el-icon><Delete /></el-icon>
              清空对话
            </el-button>
          </div>
        </div>
      </div>

      <!-- 右侧：分镜图表格 -->
      <div class="storyboard-panel" v-if="scenes.length > 0">
        <div class="panel-header">
          <h2>分镜图表格</h2>
          <el-button type="success" :loading="generatingAll" @click="generateAllImages">
            <el-icon><Picture /></el-icon>
            一键生成所有分镜图
          </el-button>
        </div>

        <el-table :data="scenes" border stripe class="storyboard-table">
          <el-table-column prop="script_overview" label="脚本概述" width="200">
            <template #default="{ row }">
              <div class="scene-info">
                <span class="time-range">{{ row.time_range }}</span>
                <span class="duration">{{ row.duration }}秒</span>
              </div>
              <p class="overview-text">{{ row.script_overview }}</p>
            </template>
          </el-table-column>

          <el-table-column label="分镜图" width="180">
            <template #default="{ row }">
              <div class="image-cell">
                <img 
                  v-if="row.image_url" 
                  :src="row.image_url" 
                  class="storyboard-image"
                  @click="previewImage(row.image_url)"
                />
                <div v-else class="image-placeholder">
                  <el-button 
                    type="primary" 
                    size="small"
                    :loading="generatingImages[row.scene_id]"
                    @click="generateImage(row)"
                  >
                    生成
                  </el-button>
                </div>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="分镜图提示词" min-width="200">
            <template #default="{ row }">
              <el-input
                v-model="row.image_prompt"
                type="textarea"
                :rows="3"
                placeholder="分镜图提示词"
                @blur="updatePrompt(row, 'image')"
              />
            </template>
          </el-table-column>

          <el-table-column label="图生视频提示词" min-width="200">
            <template #default="{ row }">
              <el-input
                v-model="row.video_prompt"
                type="textarea"
                :rows="3"
                placeholder="图生视频提示词"
                @blur="updatePrompt(row, 'video')"
              />
            </template>
          </el-table-column>

          <el-table-column label="操作" width="100" fixed="right">
            <template #default="{ row }">
              <el-button 
                type="primary" 
                size="small" 
                link
                :loading="generatingImages[row.scene_id]"
                @click="generateImage(row)"
              >
                重新生成
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 视频预览区域 -->
        <div class="video-preview-section" v-if="scenes.length > 0">
          <div class="preview-header">
            <h3>🎬 视频预览</h3>
            <div class="preview-actions">
              <el-button type="primary" @click="refreshPreview" :loading="previewLoading">
                <el-icon><Refresh /></el-icon>
                刷新预览
              </el-button>
              <el-button type="success" @click="renderVideo" :loading="renderLoading">
                <el-icon><VideoPlay /></el-icon>
                渲染导出视频
              </el-button>
            </div>
          </div>
          
          <div class="preview-container">
            <iframe 
              ref="previewIframe"
              :src="playerUrl"
              class="video-iframe"
              frameborder="0"
              allow="autoplay"
            ></iframe>
          </div>
          
          <!-- 渲染进度 -->
          <div class="render-progress" v-if="renderProgress > 0">
            <el-progress :percentage="renderProgress" :status="renderProgress >= 100 ? 'success' : ''">
              <span>{{ renderStatus }}</span>
            </el-progress>
          </div>
        </div>
      </div>
    </div>

    <!-- 图片预览 -->
    <el-dialog v-model="previewVisible" title="分镜图预览" width="80%">
      <img :src="previewUrl" class="preview-image" />
    </el-dialog>

    <!-- 渲染完成对话框 -->
    <el-dialog v-model="renderCompleteVisible" title="🎉 视频渲染完成" width="500px">
      <div class="render-complete-content">
        <el-icon class="success-icon"><SuccessFilled /></el-icon>
        <p>视频已渲染完成！</p>
        <el-button type="primary" @click="downloadVideo">
          <el-icon><Download /></el-icon>
          下载视频
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, nextTick, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'
import axios from 'axios'

interface Message {
  role: 'user' | 'assistant'
  content: string
}

interface Scene {
  scene_id: number
  time_range: string
  duration: number
  script_overview: string
  image_prompt: string
  video_prompt: string
  image_url?: string
}

const API_BASE = 'http://localhost:8000'

// 状态
const messages = ref<Message[]>([])
const inputText = ref('')
const loading = ref(false)
const sessionId = ref('')
const scenes = ref<Scene[]>([])
const generatingImages = reactive<Record<number, boolean>>({})
const generatingAll = ref(false)
const previewVisible = ref(false)
const previewUrl = ref('')
const messagesContainer = ref<HTMLElement | null>(null)

// 视频预览状态
const previewIframe = ref<HTMLIFrameElement | null>(null)
const previewLoading = ref(false)
const renderLoading = ref(false)
const renderProgress = ref(0)
const renderStatus = ref('')
const renderCompleteVisible = ref(false)
const currentRenderId = ref('')
const downloadUrl = ref('')

// Remotion Player URL (Remotion Studio 运行在 3001 端口)
const REMOTION_PLAYER_BASE = 'http://localhost:3001'

// 计算 Player URL，包含场景数据
const playerUrl = computed(() => {
  if (scenes.value.length === 0) return ''
  const scenesData = scenes.value.map(s => ({
    scene_id: s.scene_id,
    duration: s.duration,
    image_url: s.image_url || '',
    text_overlay: s.script_overview
  }))
  const encodedScenes = encodeURIComponent(JSON.stringify(scenesData))
  return `${REMOTION_PLAYER_BASE}?scenes=${encodedScenes}`
})

// 示例脚本
const exampleScripts = [
  {
    title: '秋日散步',
    content: '一个年轻女孩走在秋天的街道上，金黄的落叶飘落在她身边。她穿着米色风衣，手里拿着一杯热咖啡。阳光透过树叶洒下斑驳的光影，她微笑着享受这美好的午后时光。'
  },
  {
    title: '美食探店',
    content: '今天带大家探访一家隐藏在巷子里的宝藏餐厅。推开复古的木门，扑面而来的是浓郁的香气。老板正在厨房忙碌，锅里的食材滋滋作响。这道招牌菜色泽金黄，入口即化，让人回味无穷。'
  },
  {
    title: '产品开箱',
    content: '收到期待已久的新品！包装简约大气，打开盒子，产品静静躺在里面。拿起来手感细腻，做工精致。开机测试，界面流畅，功能强大。这次的购物体验非常满意！'
  }
]

// 发送消息
const sendMessage = async () => {
  if (!inputText.value.trim() || loading.value) return

  const userMessage = inputText.value.trim()
  messages.value.push({ role: 'user', content: userMessage })
  inputText.value = ''
  loading.value = true

  scrollToBottom()

  try {
    const response = await axios.post(`${API_BASE}/api/video-creation/chat`, {
      messages: messages.value.map(m => ({ role: m.role, content: m.content })),
      session_id: sessionId.value || null
    })

    const data = response.data
    
    if (data.success) {
      sessionId.value = data.session_id
      messages.value.push({ role: 'assistant', content: data.reply })
      
      if (data.scenes && data.scenes.length > 0) {
        scenes.value = data.scenes
      }
    } else {
      messages.value.push({ 
        role: 'assistant', 
        content: `抱歉，处理失败：${data.error || '未知错误'}` 
      })
    }
  } catch (error: any) {
    console.error('发送消息失败:', error)
    messages.value.push({ 
      role: 'assistant', 
      content: `请求失败，请检查后端服务是否正常运行。错误：${error.message}` 
    })
  } finally {
    loading.value = false
    scrollToBottom()
  }
}

// 使用示例
const useExample = (example: { title: string, content: string }) => {
  inputText.value = example.content
}

// 清空对话
const clearChat = () => {
  messages.value = []
  scenes.value = []
  sessionId.value = ''
  inputText.value = ''
}

// 滚动到底部
const scrollToBottom = async () => {
  await nextTick()
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
  }
}

// 生成单个分镜图
const generateImage = async (scene: Scene) => {
  if (!sessionId.value) {
    ElMessage.warning('请先分析脚本')
    return
  }

  generatingImages[scene.scene_id] = true

  try {
    const response = await axios.post(`${API_BASE}/api/video-creation/generate-image`, {
      session_id: sessionId.value,
      scene_id: scene.scene_id,
      prompt: scene.image_prompt,
      style: '电影感'
    })

    if (response.data.success) {
      scene.image_url = response.data.image_url
      ElMessage.success(`场景${scene.scene_id}分镜图生成成功`)
    } else {
      ElMessage.error(response.data.error || '生成失败')
    }
  } catch (error: any) {
    console.error('生成分镜图失败:', error)
    ElMessage.error(`生成失败：${error.message}`)
  } finally {
    generatingImages[scene.scene_id] = false
  }
}

// 生成所有分镜图
const generateAllImages = async () => {
  if (!sessionId.value) {
    ElMessage.warning('请先分析脚本')
    return
  }

  generatingAll.value = true
  
  try {
    const response = await axios.post(
      `${API_BASE}/api/video-creation/generate-all-images?session_id=${sessionId.value}`
    )

    if (response.data.success) {
      scenes.value = response.data.scenes
      ElMessage.success('所有分镜图生成完成')
    } else {
      ElMessage.error('批量生成失败')
    }
  } catch (error: any) {
    console.error('批量生成失败:', error)
    ElMessage.error(`生成失败：${error.message}`)
  } finally {
    generatingAll.value = false
  }
}

// 更新提示词
const updatePrompt = async (scene: Scene, type: 'image' | 'video') => {
  if (!sessionId.value) return

  try {
    await axios.post(`${API_BASE}/api/video-creation/update-prompt`, null, {
      params: {
        session_id: sessionId.value,
        scene_id: scene.scene_id,
        image_prompt: type === 'image' ? scene.image_prompt : undefined,
        video_prompt: type === 'video' ? scene.video_prompt : undefined
      }
    })
  } catch (error) {
    console.error('更新提示词失败:', error)
  }
}

// 预览图片
const previewImage = (url: string) => {
  previewUrl.value = url
  previewVisible.value = true
}

// 格式化消息
const formatMessage = (content: string) => {
  return content.replace(/\n/g, '<br>')
}

// 刷新视频预览
const refreshPreview = () => {
  previewLoading.value = true
  // 通过 postMessage 发送场景数据到 iframe
  if (previewIframe.value?.contentWindow) {
    const scenesData = scenes.value.map(s => ({
      scene_id: s.scene_id,
      duration: s.duration,
      image_url: s.image_url || '',
      text_overlay: s.script_overview
    }))
    previewIframe.value.contentWindow.postMessage({
      type: 'UPDATE_SCENES',
      scenes: scenesData
    }, '*')
  }
  setTimeout(() => {
    previewLoading.value = false
    ElMessage.success('预览已刷新')
  }, 500)
}

// 渲染视频
const renderVideo = async () => {
  if (!sessionId.value) {
    ElMessage.warning('请先分析脚本')
    return
  }

  renderLoading.value = true
  renderProgress.value = 0
  renderStatus.value = '准备渲染...'

  try {
    // 调用后端渲染 API
    const response = await axios.post(`${API_BASE}/api/video-creation/render-video`, {
      session_id: sessionId.value,
      scenes: scenes.value.map(s => ({
        scene_id: s.scene_id,
        duration: s.duration,
        image_url: s.image_url || '',
        text_overlay: s.script_overview
      }))
    })

    if (response.data.render_id) {
      currentRenderId.value = response.data.render_id
      renderStatus.value = '渲染中...'
      
      // 轮询渲染进度
      pollRenderProgress()
    } else {
      throw new Error(response.data.error || '渲染启动失败')
    }
  } catch (error: any) {
    console.error('渲染失败:', error)
    ElMessage.error(`渲染失败：${error.message}`)
    renderLoading.value = false
    renderProgress.value = 0
  }
}

// 轮询渲染进度
const pollRenderProgress = async () => {
  if (!currentRenderId.value) return

  try {
    const response = await axios.get(
      `${API_BASE}/api/video-creation/render-status/${currentRenderId.value}`
    )
    
    const data = response.data
    renderProgress.value = data.progress || 0
    renderStatus.value = data.status === 'done' ? '渲染完成' : 
                        data.status === 'rendering' ? `渲染中 ${renderProgress.value}%` :
                        data.status || '处理中...'

    if (data.status === 'done') {
      renderLoading.value = false
      downloadUrl.value = data.output_url || ''
      renderCompleteVisible.value = true
      ElMessage.success('视频渲染完成！')
    } else if (data.status === 'failed') {
      renderLoading.value = false
      ElMessage.error(`渲染失败：${data.error}`)
    } else {
      // 继续轮询
      setTimeout(pollRenderProgress, 2000)
    }
  } catch (error) {
    console.error('获取渲染进度失败:', error)
    setTimeout(pollRenderProgress, 3000)
  }
}

// 下载视频
const downloadVideo = () => {
  if (downloadUrl.value) {
    window.open(downloadUrl.value, '_blank')
    renderCompleteVisible.value = false
  } else {
    ElMessage.warning('暂无可下载的视频')
  }
}

// 监听场景变化，自动刷新预览
watch(scenes, () => {
  if (scenes.value.length > 0 && previewIframe.value?.contentWindow) {
    refreshPreview()
  }
}, { deep: true })
</script>

<style scoped>
.video-creation-page {
  padding: 20px;
  min-height: calc(100vh - 140px);
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.page-header {
  text-align: center;
  color: white;
  margin-bottom: 20px;
}

.page-header h1 {
  font-size: 28px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.page-header p {
  opacity: 0.9;
  font-size: 14px;
}

.main-content {
  display: flex;
  gap: 20px;
  max-width: 1600px;
  margin: 0 auto;
}

/* 聊天面板 */
.chat-panel {
  flex: 0 0 600px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  min-height: 600px;
  max-height: calc(100vh - 180px);
}

.chat-messages {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
}

.message {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.message.user {
  flex-direction: row-reverse;
}

.message-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.message.user .message-avatar {
  background: #409eff;
}

.message-content {
  background: #f5f7fa;
  padding: 12px 16px;
  border-radius: 12px;
  max-width: 80%;
  font-size: 14px;
  line-height: 1.6;
}

.message.user .message-content {
  background: #409eff;
  color: white;
}

.message-content ul {
  margin: 8px 0;
  padding-left: 20px;
}

.example-scripts {
  padding: 10px 20px;
  border-top: 1px solid #eee;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.example-scripts .label {
  font-size: 12px;
  color: #999;
}

.example-tag {
  cursor: pointer;
}

.example-tag:hover {
  color: #409eff;
  border-color: #409eff;
}

.chat-input {
  padding: 16px;
  border-top: 1px solid #eee;
}

.input-actions {
  margin-top: 10px;
  display: flex;
  gap: 10px;
}

/* 分镜图面板 */
.storyboard-panel {
  flex: 1;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.1);
  padding: 20px;
  overflow: auto;
  max-height: calc(100vh - 200px);
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.panel-header h2 {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.storyboard-table {
  width: 100%;
}

.scene-info {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.time-range {
  background: #409eff;
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.duration {
  background: #67c23a;
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.overview-text {
  font-size: 13px;
  color: #666;
  line-height: 1.5;
}

.image-cell {
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.storyboard-image {
  max-width: 100%;
  max-height: 100px;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.2s;
}

.storyboard-image:hover {
  transform: scale(1.05);
}

.image-placeholder {
  width: 100%;
  height: 100%;
  background: #f5f7fa;
  border: 2px dashed #ddd;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 图片预览 */
.preview-image {
  width: 100%;
  max-height: 70vh;
  object-fit: contain;
}

/* 视频预览区域 */
.video-preview-section {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.preview-header h3 {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.preview-actions {
  display: flex;
  gap: 10px;
}

.preview-container {
  width: 100%;
  aspect-ratio: 16/9;
  background: #1a1a2e;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
}

.video-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.render-progress {
  margin-top: 16px;
  padding: 12px;
  background: #f5f7fa;
  border-radius: 8px;
}

/* 渲染完成对话框 */
.render-complete-content {
  text-align: center;
  padding: 20px;
}

.render-complete-content .success-icon {
  font-size: 64px;
  color: #67c23a;
  margin-bottom: 16px;
}

.render-complete-content p {
  font-size: 18px;
  color: #333;
  margin-bottom: 20px;
}
</style>
